import React, { useState, useEffect } from "react";
import { Box, Typography, Tabs, Tab, Paper } from "@mui/material";
import {
    Dashboard as DashboardIcon,
    Store as StoreIcon,
    AccountBalance as AccountBalanceIcon,
} from "@mui/icons-material";
import { SiteSettings } from "./SiteSettings";
import { ColumnVisibilitySettings } from "../../components/settings/ColumnVisibilitySettings";
import { ProductColumnVisibilitySettings } from "../../components/settings/ProductColumnVisibilitySettings";
import { useSearchParams } from "react-router-dom";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`settings-tabpanel-${index}`}
            aria-labelledby={`settings-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `settings-tab-${index}`,
        "aria-controls": `settings-tabpanel-${index}`,
    };
}

export function Settings() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [value, setValue] = useState(0);

    // Initialize tab from URL parameter
    useEffect(() => {
        const tabParam = searchParams.get("tab");
        if (tabParam !== null) {
            const tabValue = parseInt(tabParam, 10);
            if (!isNaN(tabValue) && tabValue >= 0 && tabValue <= 2) {
                setValue(tabValue);
            }
        }
    }, [searchParams]);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        // Update URL parameter
        if (newValue === 0) {
            setSearchParams({});
        } else {
            setSearchParams({ tab: newValue.toString() });
        }
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
                {/* Header */}
                <Box
                    sx={{
                        borderBottom: 1,
                        borderColor: "divider",
                        bgcolor: "grey.50",
                    }}
                >
                    <Box sx={{ px: 3, py: 2 }}>
                        <Typography variant="h4" fontWeight={700} gutterBottom>
                            Settings
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage your admin dashboard preferences and
                            e-commerce website settings
                        </Typography>
                    </Box>

                    <Tabs
                        value={value}
                        onChange={handleChange}
                        aria-label="settings tabs"
                        sx={{ px: 3 }}
                    >
                        <Tab
                            icon={<DashboardIcon sx={{ mr: 1 }} />}
                            label="Admin Dashboard"
                            {...a11yProps(0)}
                        />
                        <Tab
                            icon={<StoreIcon sx={{ mr: 1 }} />}
                            label="E-commerce Website"
                            {...a11yProps(1)}
                        />
                        <Tab
                            icon={<AccountBalanceIcon sx={{ mr: 1 }} />}
                            label="Accounting Setup"
                            {...a11yProps(2)}
                        />
                    </Tabs>
                </Box>

                {/* Tab Panels */}
                <TabPanel value={value} index={0}>
                    <Box sx={{ px: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Dashboard Preferences
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 3 }}
                        >
                            Customize your admin dashboard experience. These
                            settings are saved locally in your browser.
                        </Typography>

                        <ColumnVisibilitySettings />

                        <Box sx={{ mt: 4 }}>
                            <ProductColumnVisibilitySettings />
                        </Box>
                    </Box>
                </TabPanel>

                <TabPanel value={value} index={1}>
                    <SiteSettings />
                </TabPanel>

                <TabPanel value={value} index={2}>
                    <SiteSettings
                        groupFilter="accounting"
                        title="Accounting Setup"
                        subtitle="Configuration used by the Assets and cash-flow tools — safe to edit without a redeploy"
                    />
                </TabPanel>
            </Paper>
        </Box>
    );
}
